import React, {FC, useEffect, useMemo, useState} from 'react'
import SearchableSelect from '../../../shared/components/SearchableSelect'

export type ProcurementExportDocType = 'REQUISITION' | 'RFQ' | 'PO' | 'GR' | 'INVOICE'

export type ProcurementExportRecord = {
  id: number
  label: string
  date?: string | null
  company?: string | null
  vessel?: string | null
  vendor?: string | null
  status?: string | null
}

export type ProcurementExportSelection = {
  documentType: ProcurementExportDocType
  fromDate?: string
  toDate?: string
  company?: string
  vessel?: string
  vendor?: string
  status?: string
  selectedRecordId?: number
  matchedRecordIds: number[]
}

interface ProcurementExportModalProps {
  visible: boolean
  onClose: () => void
  title?: string
  documentTypes: ProcurementExportDocType[]
  defaultDocumentType: ProcurementExportDocType
  records: ProcurementExportRecord[]
  loading?: boolean
  allowCompanyFilter?: boolean
  allowVesselFilter?: boolean
  allowVendorFilter?: boolean
  allowStatusFilter?: boolean
  onSubmit: (selection: ProcurementExportSelection) => void | Promise<void>
}

const labelByDocType: Record<ProcurementExportDocType, string> = {
  REQUISITION: 'Requisitions',
  RFQ: 'RFQs',
  PO: 'Purchase Orders',
  GR: 'Goods Receipts',
  INVOICE: 'Invoices',
}

const normalize = (value?: string | null) => String(value || '').trim().toLowerCase()

const ProcurementExportModal: FC<ProcurementExportModalProps> = ({
  visible,
  onClose,
  title = 'Procurement Export',
  documentTypes,
  defaultDocumentType,
  records,
  loading = false,
  allowCompanyFilter = true,
  allowVesselFilter = true,
  allowVendorFilter = true,
  allowStatusFilter = true,
  onSubmit,
}) => {
  const [documentType, setDocumentType] = useState<ProcurementExportDocType>(defaultDocumentType)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [company, setCompany] = useState('')
  const [vessel, setVessel] = useState('')
  const [vendor, setVendor] = useState('')
  const [status, setStatus] = useState('')
  const [selectedRecordId, setSelectedRecordId] = useState('')

  useEffect(() => {
    if (!visible) return
    setDocumentType(defaultDocumentType)
    setFromDate('')
    setToDate('')
    setCompany('')
    setVessel('')
    setVendor('')
    setStatus('')
    setSelectedRecordId('')
  }, [visible, defaultDocumentType])

  const companyOptions = useMemo(
    () =>
      Array.from(new Set(records.map((record) => String(record.company || '').trim()).filter(Boolean))).sort(),
    [records]
  )
  const vesselOptions = useMemo(
    () =>
      Array.from(new Set(records.map((record) => String(record.vessel || '').trim()).filter(Boolean))).sort(),
    [records]
  )
  const vendorOptions = useMemo(
    () =>
      Array.from(new Set(records.map((record) => String(record.vendor || '').trim()).filter(Boolean))).sort(),
    [records]
  )
  const statusOptions = useMemo(
    () =>
      Array.from(new Set(records.map((record) => String(record.status || '').trim()).filter(Boolean))).sort(),
    [records]
  )

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const dateValue = String(record.date || '').slice(0, 10)
      const matchesFrom = !fromDate || (dateValue && dateValue >= fromDate)
      const matchesTo = !toDate || (dateValue && dateValue <= toDate)
      const matchesCompany = !company || normalize(record.company) === normalize(company)
      const matchesVessel = !vessel || normalize(record.vessel) === normalize(vessel)
      const matchesVendor = !vendor || normalize(record.vendor) === normalize(vendor)
      const matchesStatus = !status || normalize(record.status) === normalize(status)
      return matchesFrom && matchesTo && matchesCompany && matchesVessel && matchesVendor && matchesStatus
    })
  }, [records, fromDate, toDate, company, vessel, vendor, status])

  const effectiveSelection = useMemo(() => {
    if (!selectedRecordId) return filteredRecords
    const selectedId = Number(selectedRecordId)
    return filteredRecords.filter((record) => record.id === selectedId)
  }, [filteredRecords, selectedRecordId])

  const handleDownload = async () => {
    const selectedId = selectedRecordId ? Number(selectedRecordId) : undefined
    await onSubmit({
      documentType,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      company: company || undefined,
      vessel: vessel || undefined,
      vendor: vendor || undefined,
      status: status || undefined,
      selectedRecordId: selectedId,
      matchedRecordIds: effectiveSelection.map((record) => record.id),
    })
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        <div className='modal-content bg-white text-dark' style={{color: '#181C32'}}>
          <div className='modal-header'>
            <h5 className='modal-title fw-bold text-dark'>{title}</h5>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>

          <div className='modal-body text-dark'>
            {documentTypes.length > 1 ? (
              <div className='mb-3'>
                <label className='form-label fw-semibold text-dark'>Document Type</label>
                <SearchableSelect
                  className='form-select'
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value as ProcurementExportDocType)}
                  disabled={loading}
                >
                  {documentTypes.map((type) => (
                    <option key={type} value={type}>
                      {labelByDocType[type]}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            ) : null}

            <div className='row g-3'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-dark'>From Date</label>
                <input
                  type='date'
                  className='form-control'
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-dark'>To Date</label>
                <input
                  type='date'
                  className='form-control'
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loading}
                />
              </div>

              {allowCompanyFilter ? (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-dark'>Company</label>
                  <SearchableSelect
                    className='form-select'
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    disabled={loading}
                  >
                    <option value=''>All Companies</option>
                    {companyOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              ) : null}

              {allowVesselFilter ? (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-dark'>Vessel</label>
                  <SearchableSelect
                    className='form-select'
                    value={vessel}
                    onChange={(e) => setVessel(e.target.value)}
                    disabled={loading}
                  >
                    <option value=''>All Vessels</option>
                    {vesselOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              ) : null}

              {allowVendorFilter ? (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-dark'>Vendor</label>
                  <SearchableSelect
                    className='form-select'
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    disabled={loading}
                  >
                    <option value=''>All Vendors</option>
                    {vendorOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              ) : null}

              {allowStatusFilter ? (
                <div className='col-md-6'>
                  <label className='form-label fw-semibold text-dark'>Status</label>
                  <SearchableSelect
                    className='form-select'
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={loading}
                  >
                    <option value=''>All Statuses</option>
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              ) : null}
            </div>

            <div className='mt-3'>
              <label className='form-label fw-semibold text-dark'>Specific Record (Optional)</label>
              <SearchableSelect
                className='form-select'
                value={selectedRecordId}
                onChange={(e) => setSelectedRecordId(e.target.value)}
                disabled={loading}
              >
                <option value=''>All Matched Records</option>
                {filteredRecords.map((record) => (
                  <option key={record.id} value={record.id}>
                    {record.label}
                  </option>
                ))}
              </SearchableSelect>
              <div className='form-text text-dark'>
                Matching records: <strong>{filteredRecords.length}</strong>
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => void handleDownload()}
              disabled={loading || effectiveSelection.length === 0}
            >
              {loading ? 'Preparing...' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProcurementExportModal
